import React, { useState, useRef } from 'react';
import Icon from '../../components/AppIcon';
import useTranslation from '../../hooks/useTranslation';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatDate } from '../../utils/formatDate';
import useEscapeKey from '../../hooks/useEscapeKey';

const RFIQrtResponseModal = ({ rfi, projectId, orgId, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  // 🔴 LE MODE VIENT DES DONNEES, PAS D'UN DRAPEAU.
  // Une reponse existe des qu'un des deux champs est rempli — peu importe par
  // quel chemin la modale a ete ouverte. Un drapeau passe par l'appelant
  // laissait le chemin « statut -> Repondu » ouvrir une modale vide alors que
  // la reponse etait en base : le PM croyait repartir de zero et ecrasait.
  const hasExistingResponse = !!(rfi?.response_text || rfi?.response_pdf_path);

  // En modification, la modale DOIT s'ouvrir remplie.
  const [responseText, setResponseText] = useState(rfi?.response_text || '');
  const [pdfFile, setPdfFile] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pdfInputRef = useRef(null);
  const photoInputRef = useRef(null);

  // Echap = Annuler, sauf pendant la soumission.
  useEscapeKey(onClose, !loading);

  // Le PDF deja en place : on n'a que son chemin de stockage, le nom du fichier
  // en est le dernier segment.
  const existingPdfPath = rfi?.response_pdf_path || null;
  const existingPdfName = existingPdfPath ? existingPdfPath?.split('/')?.pop() : null;
  // Photos deja enregistrees, avec leur URL signee fournie par getRFIById.
  const existingPhotos = rfi?.response_photos || [];

  // Le nom stocke est un UUID horodate
  // (« 6fb40810-8e04-…-_response_178… .pdf ») : illisible pour un humain.
  // On affiche « Reponse — 16 sept. 2026 », date a laquelle la reponse a ete
  // enregistree, et on garde le nom brut en infobulle.
  const existingPdfLabel = `${t('rfi.response')} — ${formatDate(rfi?.answered_at, language)}`;

  // URL signee DEJA produite par getRFIById (rfiService.js:71-77) et deja
  // utilisee par le panneau de detail. On la reutilise telle quelle : aucune
  // signature n'est recalculee ici.
  const openExistingPdf = () => {
    const url = rfi?.response_pdf_url;
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a?.click();
  };

  const handlePdfSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file && file?.type === 'application/pdf') {
      setPdfFile(file);
    }
    e.target.value = '';
  };

  const handlePdfDrop = (e) => {
    e?.preventDefault();
    const file = e?.dataTransfer?.files?.[0];
    if (file && file?.type === 'application/pdf') {
      setPdfFile(file);
    }
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e?.target?.files || []);
    const valid = files?.filter((f) => ['image/jpeg', 'image/png', 'image/webp']?.includes(f?.type));
    setPhotoFiles((prev) => [...prev, ...valid]);
    const newPreviews = valid?.map((f) => URL.createObjectURL(f));
    setPhotoPreviews((prev) => [...prev, ...newPreviews]);
    e.target.value = '';
  };

  const removePhoto = (idx) => {
    URL.revokeObjectURL(photoPreviews?.[idx]);
    setPhotoFiles((prev) => prev?.filter((_, i) => i !== idx));
    setPhotoPreviews((prev) => prev?.filter((_, i) => i !== idx));
  };

  // Premiere reponse : texte ET PDF obligatoires, comme avant.
  // Modification : garder le PDF existant est un choix valide.
  const isValid = responseText?.trim()?.length >= 10 && (pdfFile !== null || !!existingPdfPath);

  const handleSubmit = async () => {
    if (!isValid) return;
    setLoading(true);
    setError(null);
    try {
      const { submitQrtResponse } = await import('../../services/rfiService');
      const { data, error: submitError } = await submitQrtResponse(rfi?.id, projectId, orgId, {
        responseText: responseText?.trim(),
        responsePdfFile: pdfFile,
        responsePhotoFiles: photoFiles,
      });
      if (submitError) throw submitError;
      onSuccess?.(data);
    } catch (err) {
      console.error('QRT submit error:', err);
      setError(err?.message || t('rfi.qrt.errorFallback'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
              <Icon name="CheckCircle2" size={18} className="text-blue-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 leading-snug truncate">
                {hasExistingResponse
                  ? t('rfi.editResponse')
                  : t('rfi.qrt.title', { number: rfi?.rfi_number })}
              </h2>
              {rfi?.subject && <p className="text-[11px] text-gray-500 truncate">{rfi?.subject}</p>}
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={loading} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0">
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Bandeau d'avertissement — en modification, il rappelle que la
              reponse est REMPLACEE : il n'y a qu'une reponse, pas d'historique. */}
          <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <Icon name="AlertTriangle" size={15} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700 font-medium">
              {hasExistingResponse ? t('rfi.qrt.editWarning') : t('rfi.qrt.warning')}
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <Icon name="AlertCircle" size={15} />
              {t('rfi.qrt.errorPrefix') || 'Erreur lors de la soumission'}: {error}
            </div>
          )}

          {/* Response text */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              {t('rfi.qrt.responseText') || 'Texte de la réponse'} <span className="text-red-500">*</span>
            </label>
            <textarea
              value={responseText}
              onChange={(e) => setResponseText(e?.target?.value)}
              rows={5}
              placeholder={t('rfi.qrt.responseTextPlaceholder') || 'Copier la réponse reçue par courriel...'}
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder-gray-400 resize-none"
            />
            {responseText?.trim()?.length > 0 && responseText?.trim()?.length < 10 && (
              <p className="text-xs text-red-500 mt-1">{t('rfi.qrt.minCharacters')}</p>
            )}
          </div>

          {/* PDF upload */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              {existingPdfPath
                ? t('rfi.qrt.responsePdfOptional')
                : <>{t('rfi.qrt.responsePdf') || 'PDF de la réponse (obligatoire)'}<span className="text-red-500"> *</span></>}
            </label>
            {pdfFile ? (
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <Icon name="FileText" size={18} className="text-green-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-green-800 truncate">{pdfFile?.name}</p>
                  <p className="text-xs text-green-600">{(pdfFile?.size / 1024 / 1024)?.toFixed(2)} MB</p>
                </div>
                <button
                  type="button"
                  onClick={() => setPdfFile(null)}
                  className="p-1 rounded hover:bg-green-100 transition-colors"
                >
                  <Icon name="X" size={14} className="text-green-700" />
                </button>
              </div>
            ) : existingPdfName ? (
              <>
                {/* Bloc 1 — le document actuel. Lecture seule, cliquable. */}
                <button
                  type="button"
                  onClick={openExistingPdf}
                  title={existingPdfName}
                  className="w-full flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg text-left hover:bg-gray-100 transition-colors"
                >
                  <Icon name="FileText" size={18} className="text-gray-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                      {t('rfi.qrt.pdfAttachedDocument')}
                    </p>
                    <p className="text-sm font-medium text-gray-800 truncate">{existingPdfLabel}</p>
                  </div>
                  <Icon name="ExternalLink" size={14} className="text-gray-400 flex-shrink-0" />
                </button>

                {/* Bloc 2 — la zone de depot, qui REMPLACE. */}
                <div
                  onDrop={handlePdfDrop}
                  onDragOver={(e) => e?.preventDefault()}
                  onClick={() => pdfInputRef?.current?.click()}
                  className="mt-2 border-2 border-dashed border-gray-200 rounded-xl p-5 text-center cursor-pointer hover:border-orange-400 hover:bg-orange-50/30 transition-colors"
                >
                  <Icon name="Upload" size={22} className="text-gray-400 mx-auto mb-1.5" />
                  <p className="text-xs text-gray-600 font-medium">{t('rfi.qrt.pdfReplaceDropzone')}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{t('rfi.qrt.pdfReplaceHelper')}</p>
                </div>
              </>
            ) : (
              <div
                onDrop={handlePdfDrop}
                onDragOver={(e) => e?.preventDefault()}
                onClick={() => pdfInputRef?.current?.click()}
                className="border-2 border-dashed border-gray-200 rounded-xl p-5 text-center cursor-pointer hover:border-orange-400 hover:bg-orange-50/30 transition-colors"
              >
                <Icon name="Upload" size={22} className="text-gray-400 mx-auto mb-1.5" />
                <p className="text-xs text-gray-600 font-medium">{t('rfi.qrt.pdfDropzone')}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">{t('rfi.qrt.pdfDropzoneHint')}</p>
              </div>
            )}
            <input
              ref={pdfInputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={handlePdfSelect}
            />
          </div>

          {/* Photos upload */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              {t('rfi.qrt.responsePhotos') || 'Photos de la réponse (optionnel)'}
            </label>
            <div
              onClick={() => photoInputRef?.current?.click()}
              className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-orange-400 hover:bg-orange-50/30 transition-colors"
            >
              <Icon name="ImagePlus" size={20} className="text-gray-400 mx-auto mb-1" />
              <p className="text-xs text-gray-500">{t('rfi.qrt.photosDropzone')}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{t('rfi.qrt.photosFormats')}</p>
            </div>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={handlePhotoSelect}
            />
            {existingPhotos?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {existingPhotos?.map((photo) => (
                  <div key={photo?.id} className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                    {photo?.signed_url && (
                      <img src={photo?.signed_url} alt={photo?.caption || ''} className="w-full h-full object-cover" />
                    )}
                  </div>
                ))}
              </div>
            )}
            {photoPreviews?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {photoPreviews?.map((src, idx) => (
                  <div key={idx} className="relative group w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                    <img src={src} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(idx)}
                      className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                    >
                      <Icon name="X" size={14} className="text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-60 rounded-lg transition-colors"
          >
            {t('rfi.qrt.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid || loading}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark disabled:opacity-60 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {loading ? (
              <>
                <Icon name="Loader2" size={13} className="animate-spin" />
                {t('rfi.qrt.submitting')}
              </>
            ) : (
              <>
                <Icon name="CheckCircle2" size={13} />
                {t('rfi.qrt.submit')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RFIQrtResponseModal;
